import Show from '@/components/Show';
import useGeneratorStore from '@/state/generator-store';
import { cn } from '@/utils';
import { convertTimeToSeconds, displayDuration } from '@/utils/time';
import React, { useEffect, useState } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CircleAlert } from 'lucide-react';

interface EditingState {
  minutes: boolean;
  seconds: boolean;
}

const DurationInput: React.FC = () => {
  const [editingState, setEditingState] = useState<EditingState>({
    minutes: false,
    seconds: false,
  });
  const [isHovered, setIsHovered] = useState<{ minutes: boolean; seconds: boolean }>({
    minutes: false,
    seconds: false,
  });
  const [duration, setDuration] = useState('01:00');

  const [showTip, setShowTip] = useState(false);

  const { register, control, formValues, generatorForm } = useGeneratorStore((state) => ({
    register: state.register,
    control: state.control,
    formValues: state.formValues,
    generatorForm: state.generatorForm,
  }));

  const {
    field: { onChange },
  } = useController({
    name: 'duration',
    control,
    defaultValue: formValues?.duration,
  });

  const oldDuration = useWatch({
    control,
    name: 'duration',
    defaultValue: 0,
  });

  const { setValue } = generatorForm ?? {};

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    const [, seconds] = duration.split(':');
    const minutesNum = parseInt(value) || 0;
    const newMinutes = Math.min(99, minutesNum).toString().padStart(2, '0');

    const formattedValue = `${newMinutes}:${seconds}`;
    setDuration(formattedValue);

    const integerValue = convertTimeToSeconds(formattedValue);
    onChange(integerValue);
    setValue?.('duration', integerValue);
  };

  const handleBlur = (type: 'minutes' | 'seconds') => {
    setEditingState((prev) => ({ ...prev, [type]: false }));
  };

  const handleSecondsBlur = () => {
    const [minutes, seconds] = duration.split(':');
    const secondsNum = parseInt(seconds) || 0;
    if (secondsNum % 5 !== 0) {
      setShowTip(true);
      setTimeout(() => {
        setShowTip(false);
      }, 4000);
    }
    let roundedSeconds = Math.min(59, Math.round(secondsNum / 5) * 5);

    if (roundedSeconds === 59) {
      roundedSeconds = 55;
    }

    const newSeconds = roundedSeconds.toString().padStart(2, '0');

    const formattedValue = `${minutes}:${newSeconds}`;
    setDuration(formattedValue);

    const integerValue = convertTimeToSeconds(formattedValue);
    onChange(integerValue);
    setValue?.('duration', integerValue);

    setEditingState((prev) => ({ ...prev, seconds: false }));
  };

  const handleSecondsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    const [minutes] = duration.split(':');
    const secondsNum = parseInt(value) || 0;
    const newSeconds = Math.min(59, secondsNum).toString().padStart(2, '0');

    const formattedValue = `${minutes}:${newSeconds}`;
    setDuration(formattedValue);
  };

  const [minutes, seconds] = duration.split(':');

  const handleMinutesFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleSecondsFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleMinutesOnClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingState((prev) => ({ ...prev, minutes: true }));
    setIsHovered((prev) => ({ ...prev, minutes: false }));
  };

  const handleSecondsOnClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingState((prev) => ({ ...prev, seconds: true }));
    setIsHovered((prev) => ({ ...prev, seconds: false }));
  };

  useEffect(() => {
    register('duration');
    setDuration(displayDuration(oldDuration));
  }, [register]);

  return (
    <div className="flex flex-col items-start">
      <span className="text-[10px] text-[#9D9DA6] uppercase">DURATION</span>
      <div className="flex items-center">
        <input
          type="text"
          value={minutes}
          onChange={handleMinutesChange}
          onFocus={handleMinutesFocus}
          onBlur={() => handleBlur('minutes')}
          className={cn(
            'bg-transparent text-[1rem] outline-none w-6 text-center',
            editingState.minutes
              ? ['text-jen-orange', 'border border-orange-500/50 rounded', 'block']
              : [isHovered.minutes ? 'text-jen-orange' : 'text-[#9D9DA6]', 'border-none', 'cursor-pointer'],
          )}
          tabIndex={1}
          onClick={handleMinutesOnClick}
          onMouseEnter={() => setIsHovered((prev) => ({ ...prev, minutes: true }))}
          onMouseLeave={() => setIsHovered((prev) => ({ ...prev, minutes: false }))}
        />
        <span className={isHovered.minutes || isHovered.seconds ? 'text-jen-orange' : 'text-[#9D9DA6]'}>:</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <input
                type="text"
                value={seconds}
                onChange={handleSecondsChange}
                onFocus={handleSecondsFocus}
                onBlur={handleSecondsBlur}
                className={cn(
                  'bg-transparent text-[1rem] outline-none w-6 text-center',
                  editingState.seconds
                    ? ['text-jen-orange', 'border border-orange-500/50 rounded', 'block']
                    : [isHovered.seconds ? 'text-jen-orange' : 'text-[#9D9DA6]', 'border-none', 'cursor-pointer'],
                )}
                tabIndex={2}
                onClick={handleSecondsOnClick}
                onMouseEnter={() => setIsHovered((prev) => ({ ...prev, seconds: true }))}
                onMouseLeave={() => setIsHovered((prev) => ({ ...prev, seconds: false }))}
              />
            </TooltipTrigger>
            <TooltipContent className="bg-black text-[#9D9DA6] border-none" open={showTip}>
              <div className="flex items-center gap-2">
                <CircleAlert color="#FF5B27" size={16} />
                <span>Seconds must be in multiples of 5</span>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default DurationInput;
